import { useState } from 'react';
import { observer } from 'mobx-react';
import { Input, Tag } from 'antd';

// Free-form tags: type and press Enter (or leave the box) to add one, × to remove one. Used by the scenario
// library's filter and by the scenario editor, which saves tags on their own.
//
// We avoid antd's <Select mode="tags"> because its dropdown is heavy when
// you don't have a known option list — and we want any free string to be
// a tag here. A small Input + chip array is lighter and matches Linear /
// GitHub-style "type and Enter to add" semantics.

interface Props {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  size?: 'small' | 'middle';
  inputWidth?: number;
  disabled?: boolean;
}

export const TagsInput = observer(function TagsInput({
  value,
  onChange,
  placeholder = 'Tags…',
  size = 'small',
  inputWidth = 140,
  disabled = false,
}: Props) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (value.includes(t)) {
      setDraft('');
      return;
    }
    onChange([...value, t]);
    setDraft('');
  };

  const remove = (t: string) => onChange(value.filter((x) => x !== t));

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      <Input
        size={size}
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onPressEnter={add}
        onBlur={add}
        style={{ width: inputWidth }}
      />
      {value.map((t) => (
        <Tag
          key={t}
          closable={!disabled}
          onClose={(e) => {
            e.preventDefault();
            remove(t);
          }}
        >
          {t}
        </Tag>
      ))}
    </span>
  );
});
